import * as React from "react";
import { cn } from "@/lib/utils";

const Table = React.forwardRef<
  HTMLTableElement,
  React.HTMLAttributes<HTMLTableElement>
>(({ className, ...props }, ref) => (
  <table
    ref={ref}
    className={cn("caption-bottom text-body-2", className)}
    {...props}
  />
));
Table.displayName = "Table";

const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead ref={ref} className={cn(className)} {...props} />
));
TableHeader.displayName = "TableHeader";

const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody
    ref={ref}
    className={cn(className)}
    {...props}
  />
));
TableBody.displayName = "TableBody";

const TableFooter = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn(
      "bg-muted/50 font-medium shadow-[inset_0_1px_0_0_var(--lines-solid-normal)]",
      className
    )}
    {...props}
  />
));
TableFooter.displayName = "TableFooter";

const TableRow = React.forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement>
>(({ className, ...props }, ref) => (
  <tr
    ref={ref}
    className={cn(
      // 드로어가 열린 행(트리거 마커 또는 selected)은 hover 색이 하이라이트를 덮지 않게 한다
      "transition-colors not-has-[[data-row-drawer-open]]:not-data-[state=selected]:hover:bg-fills-light",
      "has-[[data-row-drawer-open]]:bg-fills-highlight data-[state=selected]:bg-fills-highlight",
      className
    )}
    {...props}
  />
));
TableRow.displayName = "TableRow";

type StrokeVariant = "none" | "right" | "bottom" | "right-bottom";

interface TableHeadProps extends React.ThHTMLAttributes<HTMLTableCellElement> {
  stroke?: StrokeVariant;
}

const TableHead = React.forwardRef<HTMLTableCellElement, TableHeadProps>(
  ({ className, stroke = "right-bottom", ...props }, ref) => {
    const strokeStyles = {
      none: "",
      right: "shadow-[inset_-1px_0_0_0_var(--lines-solid-normal)]",
      bottom: "shadow-[inset_0_-1px_0_0_var(--lines-solid-normal)]",
      "right-bottom":
        "shadow-[inset_-1px_0_0_0_var(--lines-solid-normal),inset_0_-1px_0_0_var(--lines-solid-normal)]",
    };

    return (
      <th
        ref={ref}
        className={cn(
          "h-10 px-3 py-2 text-left align-middle typo-body-2-semibold text-labels-primary bg-fills-light [&:has([role=checkbox])]:pr-0",
          strokeStyles[stroke],
          className
        )}
        {...props}
      />
    );
  }
);
TableHead.displayName = "TableHead";

interface TableCellProps extends React.TdHTMLAttributes<HTMLTableCellElement> {
  stroke?: StrokeVariant;
}

const TableCell = React.forwardRef<HTMLTableCellElement, TableCellProps>(
  ({ className, stroke = "right-bottom", ...props }, ref) => {
    const strokeStyles = {
      none: "",
      right: "shadow-[inset_-1px_0_0_0_var(--lines-solid-normal)]",
      bottom: "shadow-[inset_0_-1px_0_0_var(--lines-solid-normal)]",
      "right-bottom":
        "shadow-[inset_-1px_0_0_0_var(--lines-solid-normal),inset_0_-1px_0_0_var(--lines-solid-normal)]",
    };

    return (
      <td
        ref={ref}
        className={cn(
          "h-10 px-3 py-2 align-middle typo-body-2-medium text-labels-primary [&:has([role=checkbox])]:pr-0",
          strokeStyles[stroke],
          className
        )}
        {...props}
      />
    );
  }
);
TableCell.displayName = "TableCell";

const TableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  React.HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
  <caption
    ref={ref}
    className={cn("mt-4 text-sm text-muted-foreground", className)}
    {...props}
  />
));
TableCaption.displayName = "TableCaption";

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
};
